import {
  hydrateAccessTokenFromStorage,
  isTransientAuthRefreshError,
  refreshAccessToken,
} from './authUtils';
import { COMPANY_ID_HEADER, getReactAppApiUrl } from './constants';
import { cacheData, getCachedData, generateCacheKey } from './offlineSyncUtils';
import { notifyDashboardCacheIfNeeded } from './dashboardCacheEvents';
import { toUserFacingHttpFailureMessage } from './userFacingHttpErrors';

export {
  toUserFacingHttpFailureMessage,
  toUserFacingHttpFailureMessageFromUnknown,
} from './userFacingHttpErrors';

/** JSON body from a failed API response (e.g. Nest `BadRequestException` object). */
export type ClientHttpErrorBody = Record<string, unknown> & {
  message?: string | string[];
  detail?: string;
  code?: string;
  statusCode?: number;
  errors?: string[];
};

function messageFromHttpErrorBody(
  parsedBody: ClientHttpErrorBody | undefined,
  fallback: string,
): string {
  if (!parsedBody) {
    return fallback;
  }
  if (parsedBody.detail && typeof parsedBody.detail === 'string') {
    return parsedBody.detail;
  }

  let message = fallback;
  if (typeof parsedBody.message === 'string' && parsedBody.message.trim()) {
    message = parsedBody.message;
  } else if (
    Array.isArray(parsedBody.message) &&
    parsedBody.message.length > 0
  ) {
    message = parsedBody.message.join(', ');
  }

  if (Array.isArray(parsedBody.errors) && parsedBody.errors.length > 0) {
    const details = parsedBody.errors
      .filter(
        (entry): entry is string =>
          typeof entry === 'string' && entry.trim() !== '',
      )
      .join(' ');
    if (details) {
      return message && message !== fallback
        ? `${message}: ${details}`
        : details;
    }
  }

  return message;
}

/** Thrown by `clientAuthorizedPost` (and similar) when `response.ok` is false; carries the parsed JSON body when available. */
export class ClientHttpError extends Error {
  readonly status: number;
  readonly responseBody: ClientHttpErrorBody | undefined;

  constructor(
    message: string,
    status: number,
    responseBody?: ClientHttpErrorBody,
  ) {
    super(message);
    this.name = 'ClientHttpError';
    this.status = status;
    this.responseBody = responseBody;
  }
}

/**
 * Check if the browser is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/** Endpoints that must not trigger session refresh retry on 401. */
function isAuthTokenEndpoint(endpoint: string): boolean {
  const clean = endpoint.replace(/^\/+/, '').toLowerCase();
  return (
    clean === 'auth/token' ||
    clean.endsWith('/auth/token') ||
    clean === 'auth/refresh-session' ||
    clean.endsWith('/auth/refresh-session') ||
    clean === 'auth/exchange' ||
    clean.endsWith('/auth/exchange')
  );
}

/**
 * Attach `X-Company-Id` when the caller supplies a company scope (replaces query `companyId`).
 */
function attachCompanyIdHeader(
  headers: Record<string, string>,
  endpoint: string,
  companyId?: string | null,
): void {
  if (!companyId) return;
  const basePath = endpoint.replace(/^\/+/, '').split('?')[0];
  if (basePath.includes('/companyId/')) return;
  headers[COMPANY_ID_HEADER] = companyId;
}

/**
 * On 401, attempt to refresh the access token and retry the request once.
 * Returns the response (possibly from retry). Throws if refresh fails.
 */
async function ensureValidTokenAndRetryOn401(
  endpoint: string,
  companyId: string | null | undefined,
  doRequest: (headers: Record<string, string>) => Promise<Response>,
): Promise<Response> {
  let headers = await getAuthHeaders();
  attachCompanyIdHeader(headers, endpoint, companyId);
  let res = await doRequest(headers);
  if (res.status === 401 && !isAuthTokenEndpoint(endpoint)) {
    try {
      await refreshAccessToken({ force: true });
    } catch (error) {
      if (isTransientAuthRefreshError(error)) {
        throw error;
      }
      throw new Error('Session expired. Please log in again.', {
        cause: error,
      });
    }
    headers = await getAuthHeaders();
    attachCompanyIdHeader(headers, endpoint, companyId);
    res = await doRequest(headers);
  }
  return res;
}

/**
 * Get authorization header with access token
 * Automatically refreshes token if expired
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    accept: 'application/json',
  };

  // In Auth mode, auth flows via HttpOnly cookies.
  // Keep this call to hydrate auth marker for route guards/UI state.
  await hydrateAccessTokenFromStorage();

  return headers;
}

/**
 * Get a valid access token using the same logic as REST (hydrate + refresh if needed).
 * Use this for WebSocket handshake so the token is identical to Authorization: Bearer.
 * Returns null if no token can be obtained (e.g. refresh failed).
 */
export function getValidAccessToken(): Promise<string | null> {
  // Cookie-based auth does not expose access token to JS.
  return Promise.resolve(null);
}

export interface AuthorizedFetchOptions {
  /** Endpoint path (e.g. 'chat') for 401 retry; omit for no retry. */
  endpoint?: string;
  /** If true, do not add auth headers (e.g. presigned URLs). */
  skipAuth?: boolean;
  /** Active company UUID (`X-Company-Id`); use with `endpoint` for header scoping. */
  companyId?: string | null;
}

/**
 * Authorized fetch: adds auth headers and optional 401 retry.
 * Use this instead of global fetch for all API and upload/download requests.
 * Use skipAuth: true for presigned URLs that must not include Authorization.
 */
export async function authorizedFetch(
  url: string,
  init: RequestInit = {},
  options: AuthorizedFetchOptions = {},
): Promise<Response> {
  if (options.skipAuth) {
    return fetch(url, { ...init, credentials: 'omit' });
  }
  const authHeaders = await getAuthHeaders();
  const existingHeaders =
    init.headers instanceof Headers
      ? Object.fromEntries(init.headers.entries())
      : ((init.headers as Record<string, string> | undefined) ?? {});
  const mergedHeaders = { ...authHeaders, ...existingHeaders };
  if (options.endpoint) {
    attachCompanyIdHeader(mergedHeaders, options.endpoint, options.companyId);
  }
  let res = await fetch(url, {
    ...init,
    headers: mergedHeaders,
    credentials: 'include',
  });
  if (
    res.status === 401 &&
    options.endpoint &&
    !isAuthTokenEndpoint(options.endpoint)
  ) {
    try {
      await refreshAccessToken({ force: true });
    } catch (error) {
      if (isTransientAuthRefreshError(error)) {
        throw error;
      }
      throw new Error('Session expired. Please log in again.', {
        cause: error,
      });
    }
    const auth2 = await getAuthHeaders();
    const merged2 = { ...auth2, ...existingHeaders };
    if (options.endpoint) {
      attachCompanyIdHeader(merged2, options.endpoint, options.companyId);
    }
    res = await fetch(url, {
      ...init,
      headers: merged2,
      credentials: 'include',
    });
  }
  return res;
}

/**
 * Builds request URL with query parameters. Company scope is sent via `X-Company-Id`, not the query string.
 */
function buildUrlWithQueryParams(
  endpoint: string,
  queryParams?: Record<string, string>,
): string {
  const cleanEndpoint = endpoint.replace(/^\/+/, '');

  const [basePath, existingQuery] = cleanEndpoint.split('?');
  let url = `${getReactAppApiUrl()}/${basePath}`;

  const params = new URLSearchParams();

  if (existingQuery) {
    const existingParams = new URLSearchParams(existingQuery);
    existingParams.forEach((value, key) => {
      params.append(key, value);
    });
  }

  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
  }

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
}

export const clientAuthorizedGet = async <T>(
  endpoint: string,
  queryParams?: Record<string, string>,
  companyId?: string | null,
  options?: {
    /** Do not read/write IndexedDB offline cache (use for signed URLs / replaceable assets). */
    skipOfflineCache?: boolean;
    /** Fetch cache mode; prefer `no-store` for fresh signed URLs. */
    cache?: RequestCache;
    signal?: AbortSignal;
  },
): Promise<T> => {
  const cacheKey = generateCacheKey(endpoint, queryParams, companyId);
  const skipOfflineCache = options?.skipOfflineCache === true;
  const fetchCache = options?.cache;

  // If offline, try to serve from cache
  if (!isOnline()) {
    if (!skipOfflineCache) {
      const cached = (await getCachedData(cacheKey)) as T | null;
      if (cached) {
        console.log(`Serving offline data for: ${endpoint}`);
        return cached;
      }
    }
    throw new Error(
      'Offline: No cached data available for this request. Please connect to the internet.',
    );
  }

  const url = buildUrlWithQueryParams(endpoint, queryParams);

  try {
    const res = await ensureValidTokenAndRetryOn401(
      endpoint,
      companyId,
      (headers) =>
        fetch(url, {
          method: 'GET',
          headers,
          credentials: 'include',
          ...(fetchCache ? { cache: fetchCache } : {}),
          ...(options?.signal ? { signal: options.signal } : {}),
        }),
    );

    if (!res.ok) {
      let errorMessage = 'An error occurred while processing your request.';
      let parsedBody: ClientHttpErrorBody | undefined;
      try {
        const text = await res.text();
        if (text && text.trim() !== '') {
          parsedBody = JSON.parse(text) as ClientHttpErrorBody;
          errorMessage = messageFromHttpErrorBody(parsedBody, errorMessage);
        }
      } catch {
        errorMessage = res.statusText || errorMessage;
      }
      throw new ClientHttpError(
        toUserFacingHttpFailureMessage(res.status, errorMessage),
        res.status,
        parsedBody,
      );
    }

    const text = await res.text();
    if (!text || text.trim() === '') {
      if (!skipOfflineCache) {
        void cacheData(cacheKey, null);
      }
      return null as T;
    }
    const resBody = JSON.parse(text) as T;

    // Success! Cache the data for offline use
    if (!skipOfflineCache) {
      void cacheData(cacheKey, resBody);
    }

    return resBody;
  } catch (error) {
    // If it's a network error that happened even though isOnline() was true
    // (e.g. DNS failure, or connection dropped just now)
    if (error instanceof TypeError && !skipOfflineCache) {
      const cached = (await getCachedData(cacheKey)) as T | null;
      if (cached) {
        console.log(`Network failed, serving offline cache for: ${endpoint}`);
        return cached;
      }
    }

    // Handle network errors (including CORS, connection refused, etc.)
    if (error instanceof TypeError) {
      if (
        error.message === 'Failed to fetch' ||
        error.message.includes('fetch')
      ) {
        throw new Error(
          'Network error: Unable to connect to the server. Please check your connection and try again.',
          { cause: error },
        );
      }
    }
    // Re-throw other errors (including Error instances we created)
    if (error instanceof Error) {
      throw error;
    }
    // Handle any other unexpected errors
    throw new Error(
      'An unexpected error occurred while processing your request.',
      { cause: error },
    );
  }
};

export const clientAuthorizedGetBlob = async (
  endpoint: string,
  queryParams?: Record<string, string>,
  companyId?: string | null,
): Promise<{ blob: Blob; contentDisposition: string | null }> => {
  const url = buildUrlWithQueryParams(endpoint, queryParams);
  const res = await ensureValidTokenAndRetryOn401(
    endpoint,
    companyId,
    (headers) =>
      fetch(url, {
        method: 'GET',
        headers,
        credentials: 'include',
        cache: 'no-store',
      }),
  );
  if (!res.ok) {
    let errorMessage = 'An error occurred while processing your request.';
    try {
      const body = (await res.json()) as ClientHttpErrorBody;
      errorMessage = messageFromHttpErrorBody(body, errorMessage);
    } catch {
      errorMessage = res.statusText || errorMessage;
    }
    throw new ClientHttpError(
      toUserFacingHttpFailureMessage(res.status, errorMessage),
      res.status,
    );
  }
  return {
    blob: await res.blob(),
    contentDisposition: res.headers.get('content-disposition'),
  };
};

/**
 * Public GET request without authentication.
 * Does not use auth headers or offline cache. Pass `companyId` to set `X-Company-Id`.
 */
export const clientPublicGet = async <T>(
  endpoint: string,
  companyId?: string | null,
  extraHeaders?: Record<string, string>,
  options?: { credentials?: RequestCredentials },
): Promise<T> => {
  const baseUrl = getReactAppApiUrl().replace(/\/+$/, '');
  const path = endpoint.replace(/^\/+/, '');
  const url = path ? `${baseUrl}/${path}` : baseUrl;

  const headers: Record<string, string> = {
    accept: 'application/json',
    ...extraHeaders,
  };
  attachCompanyIdHeader(headers, endpoint, companyId ?? undefined);

  const res = await fetch(url, {
    method: 'GET',
    headers,
    credentials: options?.credentials ?? 'omit',
  });

  if (!res.ok) {
    const errorMessage = await parsePublicErrorBody(res);
    throw new ClientHttpError(errorMessage, res.status);
  }

  return (await res.json()) as T;
};

/**
 * Helper to parse error responses from public endpoints.
 */
const parsePublicErrorBody = async (res: Response): Promise<string> => {
  let errorMessage = 'An error occurred.';
  try {
    const resBody = (await res.json()) as ClientHttpErrorBody;
    errorMessage = messageFromHttpErrorBody(resBody, errorMessage);
  } catch {
    errorMessage = res.statusText || errorMessage;
  }
  return errorMessage;
};

/**
 * Public POST request without authentication.
 * Supports custom headers (e.g. `x-shop-session` for guest cart tracking).
 */
export const clientPublicPost = async <T>(
  endpoint: string,
  data?: Record<string, unknown>,
  extraHeaders?: Record<string, string>,
): Promise<T> => {
  const baseUrl = getReactAppApiUrl().replace(/\/+$/, '');
  const path = endpoint.replace(/^\/+/, '');
  const url = path ? `${baseUrl}/${path}` : baseUrl;

  const headers: Record<string, string> = {
    accept: 'application/json',
    'Content-Type': 'application/json',
    ...extraHeaders,
  };

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!res.ok) {
    const errorMessage = await parsePublicErrorBody(res);
    throw new ClientHttpError(errorMessage, res.status);
  }

  return (await res.json()) as T;
};

/**
 * Public PATCH request without authentication.
 */
export const clientPublicPatch = async <T>(
  endpoint: string,
  data?: Record<string, unknown>,
  extraHeaders?: Record<string, string>,
): Promise<T> => {
  const baseUrl = getReactAppApiUrl().replace(/\/+$/, '');
  const path = endpoint.replace(/^\/+/, '');
  const url = path ? `${baseUrl}/${path}` : baseUrl;

  const headers: Record<string, string> = {
    accept: 'application/json',
    'Content-Type': 'application/json',
    ...extraHeaders,
  };

  const res = await fetch(url, {
    method: 'PATCH',
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  if (!res.ok) {
    const errorMessage = await parsePublicErrorBody(res);
    throw new ClientHttpError(errorMessage, res.status);
  }

  return (await res.json()) as T;
};

/**
 * Public DELETE request without authentication.
 */
export const clientPublicDelete = async <T>(
  endpoint: string,
  extraHeaders?: Record<string, string>,
): Promise<T> => {
  const baseUrl = getReactAppApiUrl().replace(/\/+$/, '');
  const path = endpoint.replace(/^\/+/, '');
  const url = path ? `${baseUrl}/${path}` : baseUrl;

  const headers: Record<string, string> = {
    accept: 'application/json',
    ...extraHeaders,
  };

  const res = await fetch(url, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok) {
    const errorMessage = await parsePublicErrorBody(res);
    throw new ClientHttpError(errorMessage, res.status);
  }

  return (await res.json()) as T;
};

export const clientAuthorizedPost = async <T>(
  endpoint: string,
  data?: Record<string, unknown>,
  companyId?: string | null,
  signal?: AbortSignal,
): Promise<T> => {
  try {
    const url = buildUrlWithQueryParams(endpoint);
    const res = await ensureValidTokenAndRetryOn401(
      endpoint,
      companyId,
      async (headers) => {
        headers['Content-Type'] = 'application/json';
        return fetch(url, {
          method: 'POST',
          headers,
          credentials: 'include',
          ...(signal ? { signal } : {}),
          ...(!data ? {} : { body: JSON.stringify(data) }),
        });
      },
    );

    if (!res.ok) {
      let errorMessage = 'An error occurred while processing your request.';
      let parsedBody: ClientHttpErrorBody | undefined;
      try {
        parsedBody = (await res.json()) as ClientHttpErrorBody;
        errorMessage = messageFromHttpErrorBody(parsedBody, errorMessage);
      } catch {
        errorMessage = res.statusText || errorMessage;
      }
      throw new ClientHttpError(
        toUserFacingHttpFailureMessage(res.status, errorMessage),
        res.status,
        parsedBody,
      );
    }

    const resBody = (await res.json()) as T;
    notifyDashboardCacheIfNeeded('POST', endpoint, companyId);
    return resBody;
  } catch (error) {
    // Handle network errors (including CORS, connection refused, etc.)
    if (error instanceof TypeError) {
      if (
        error.message === 'Failed to fetch' ||
        error.message.includes('fetch')
      ) {
        throw new Error(
          'Network error: Unable to connect to the server. Please check your connection and try again.',
          { cause: error },
        );
      }
    }
    // Re-throw HTTP errors and other Error instances we created
    if (error instanceof Error) {
      throw error;
    }
    // Handle any other unexpected errors
    throw new Error(
      'An unexpected error occurred while processing your request.',
      { cause: error },
    );
  }
};

export const clientAuthorizedPatch = async <T>(
  endpoint: string,
  data?: Record<string, unknown>,
  companyId?: string | null,
): Promise<T> => {
  try {
    const url = buildUrlWithQueryParams(endpoint);
    const res = await ensureValidTokenAndRetryOn401(
      endpoint,
      companyId,
      async (headers) => {
        headers['Content-Type'] = 'application/json';
        return fetch(url, {
          method: 'PATCH',
          headers,
          credentials: 'include',
          ...(!data ? {} : { body: JSON.stringify(data) }),
        });
      },
    );

    if (!res.ok) {
      let errorMessage = 'An error occurred while processing your request.';
      try {
        const resBody = (await res.json()) as ClientHttpErrorBody;
        errorMessage = messageFromHttpErrorBody(resBody, errorMessage);
      } catch {
        errorMessage = res.statusText || errorMessage;
      }
      throw new Error(toUserFacingHttpFailureMessage(res.status, errorMessage));
    }

    const resBody = (await res.json()) as T;
    notifyDashboardCacheIfNeeded('PATCH', endpoint, companyId);
    return resBody;
  } catch (error) {
    // Handle network errors (including CORS, connection refused, etc.)
    if (error instanceof TypeError) {
      if (
        error.message === 'Failed to fetch' ||
        error.message.includes('fetch')
      ) {
        throw new Error(
          'Network error: Unable to connect to the server. Please check your connection and try again.',
          { cause: error },
        );
      }
    }
    // Re-throw other errors (including Error instances we created)
    if (error instanceof Error) {
      throw error;
    }
    // Handle any other unexpected errors
    throw new Error(
      'An unexpected error occurred while processing your request.',
      { cause: error },
    );
  }
};

export const clientAuthorizedPut = async <T>(
  endpoint: string,
  data?: Record<string, unknown>,
  companyId?: string | null,
): Promise<T> => {
  try {
    const url = buildUrlWithQueryParams(endpoint);
    const res = await ensureValidTokenAndRetryOn401(
      endpoint,
      companyId,
      async (headers) => {
        headers['Content-Type'] = 'application/json';
        return fetch(url, {
          method: 'PUT',
          headers,
          credentials: 'include',
          ...(!data ? {} : { body: JSON.stringify(data) }),
        });
      },
    );

    if (!res.ok) {
      let errorMessage = 'An error occurred while processing your request.';
      try {
        const resBody = (await res.json()) as ClientHttpErrorBody;
        errorMessage = messageFromHttpErrorBody(resBody, errorMessage);
      } catch {
        errorMessage = res.statusText || errorMessage;
      }
      throw new Error(toUserFacingHttpFailureMessage(res.status, errorMessage));
    }

    const resBody = (await res.json()) as T;
    notifyDashboardCacheIfNeeded('PUT', endpoint, companyId);
    return resBody;
  } catch (error) {
    // Handle network errors (including CORS, connection refused, etc.)
    if (error instanceof TypeError) {
      if (
        error.message === 'Failed to fetch' ||
        error.message.includes('fetch')
      ) {
        throw new Error(
          'Network error: Unable to connect to the server. Please check your connection and try again.',
          { cause: error },
        );
      }
    }
    // Re-throw other errors (including Error instances we created)
    if (error instanceof Error) {
      throw error;
    }
    // Handle any other unexpected errors
    throw new Error(
      'An unexpected error occurred while processing your request.',
      { cause: error },
    );
  }
};

/**
 * Authorized DELETE. `companyId` is the **third** argument (`X-Company-Id` header).
 * Do not pass a company UUID as `data` — that becomes a JSON string body and the API returns 400.
 */
export const clientAuthorizedDelete = async <T>(
  endpoint: string,
  data?: Record<string, unknown>,
  companyId?: string | null,
  queryParams?: Record<string, string>,
): Promise<T | null> => {
  try {
    const url = buildUrlWithQueryParams(endpoint, queryParams);
    const res = await ensureValidTokenAndRetryOn401(
      endpoint,
      companyId,
      async (headers) => {
        headers['Content-Type'] = 'application/json';
        return fetch(url, {
          method: 'DELETE',
          headers,
          credentials: 'include',
          ...(!data ? {} : { body: JSON.stringify(data) }),
        });
      },
    );

    if (res.status === 204) {
      notifyDashboardCacheIfNeeded('DELETE', endpoint, companyId);
      return null; // No content (e.g., resource deleted successfully)
    }

    if (!res.ok) {
      let errorMessage = 'An error occurred while processing your request.';
      try {
        const resBody = (await res.json()) as ClientHttpErrorBody;
        errorMessage = messageFromHttpErrorBody(resBody, errorMessage);
      } catch {
        errorMessage = res.statusText || errorMessage;
      }
      throw new Error(toUserFacingHttpFailureMessage(res.status, errorMessage));
    }

    // Check if response has content before parsing JSON
    const contentLength = res.headers.get('content-length');
    const contentType = res.headers.get('content-type');

    // If content-length is 0 or response doesn't have JSON content-type, return null
    if (
      contentLength === '0' ||
      (contentLength === null && !contentType?.includes('application/json'))
    ) {
      // Try to read text to confirm it's empty
      const text = await res.text();
      if (!text || text.trim() === '') {
        return null;
      }
      // If there's text but it's not JSON, try to parse it
      try {
        return JSON.parse(text) as T;
      } catch {
        return null;
      }
    }

    // Response has content, parse as JSON
    const resBody = (await res.json()) as T;
    notifyDashboardCacheIfNeeded('DELETE', endpoint, companyId);
    return resBody;
  } catch (error) {
    // Handle network errors (including CORS, connection refused, etc.)
    if (error instanceof TypeError) {
      if (
        error.message === 'Failed to fetch' ||
        error.message.includes('fetch')
      ) {
        throw new Error(
          'Network error: Unable to connect to the server. Please check your connection and try again.',
          { cause: error },
        );
      }
    }
    // Re-throw other errors (including Error instances we created)
    if (error instanceof Error) {
      throw error;
    }
    // Handle any other unexpected errors
    throw new Error(
      'An unexpected error occurred while processing your request.',
      { cause: error },
    );
  }
};
